import { createRouter, createWebHistory } from 'vue-router'
import Home from '../pages/Home.vue'
import Vehicles from '../pages/Vehicles.vue'
import Locations from '../pages/Locations.vue'
import Profile from '../pages/Profile.vue'
import Analytics from '../pages/Analytics.vue'
import Login from '../pages/Login.vue'
import History from '../pages/History.vue'
import Pending from '../pages/Pending.vue'
import NoAccess from '../pages/NoAccess.vue'
import RouteEditor from '../pages/RouteEditor.vue'
import AITools from '../pages/AITools.vue'
import AuthService from '../services/AuthService'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresGuest: true }
  },
  {
    path: '/pending',
    name: 'Pending',
    component: Pending,
    meta: { requiresAuth: true }
  },
  {
    path: '/no-access',
    name: 'NoAccess',
    component: NoAccess,
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/home',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true, requiresPermission: 'home' }
  },
  {
    path: '/vehicles',
    name: 'Vehicles',
    component: Vehicles,
    meta: { requiresAuth: true, requiresPermission: 'vehicles' }
  },
  {
    path: '/locations',
    name: 'Locations',
    component: Locations,
    meta: { requiresAuth: true, requiresPermission: 'locations' }
  },
  {
    path: '/locations/:id',
    name: 'LocationDetail',
    component: Locations,
    meta: { requiresAuth: true, requiresPermission: 'locations' }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true }
  },
  {
    path: '/analytics',
    name: 'Analytics',
    component: Analytics,
    meta: { requiresAuth: true, requiresPermission: 'analytics' }
  },
  {
    path: '/analytics-v2',
    name: 'AnalyticsV2',
    component: () => import('../pages/AnalyticsV2.vue'),
    meta: { requiresAuth: true, requiresPermission: 'analytics' }
  },
  {
    path: '/history',
    name: 'History',
    component: History,
    meta: { requiresAuth: true, requiresPermission: 'history' }
  },
  {
    path: '/route-editor/:id',
    name: 'RouteEditor',
    component: RouteEditor,
    meta: { requiresAuth: true, requiresPermission: 'history' }
  },
  {
    path: '/ai-tools',
    name: 'AITools',
    component: AITools,
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Wait for auth to be initialized before checking authentication
  const isAuthenticated = await AuthService.waitForAuthInitialization()
  
  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // User is not authenticated, redirect to login
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else {
      const user = AuthService.getCurrentUser()
      
      try {
        // Get user status including email verification and permissions
        const userStatus = await AuthService.getUserStatus(user.uid)
        
        // Update email verification status in database if it has changed
        if (user.emailVerified !== userStatus.emailVerified) {
          await AuthService.updateEmailVerificationStatus(user.uid, user.emailVerified)
          userStatus.emailVerified = user.emailVerified
        }
        
        // Check if email is verified
        if (!userStatus.emailVerified) {
          // Email not verified, redirect to pending page
          if (to.path !== '/pending') {
            next('/pending')
            return
          }
        }
        
        // Check if user has any permissions
        const hasAnyPermissions = Object.values(userStatus.permissions).some(p => p === true)
        if (!hasAnyPermissions) {
          // No permissions, redirect to pending page
          if (to.path !== '/pending') {
            next('/pending')
            return
          }
        }
        
        // Check if route requires specific permission
        const requiredPermission = to.meta.requiresPermission
        if (requiredPermission) {
          if (!userStatus.permissions[requiredPermission]) {
            // User doesn't have required permission
            next({
              path: '/no-access',
              query: { page: requiredPermission }
            })
            return
          }
        }
        
        // Special handling for home route - redirect to first available page
        if ((to.path === '/home' || to.path === '/') && !userStatus.permissions.home) {
          const availablePages = Object.keys(userStatus.permissions).filter(page => userStatus.permissions[page])
          if (availablePages.length > 0) {
            next(`/${availablePages[0]}`)
            return
          }
        }
        
        next()
      } catch (error) {
        console.error('Error in route guard:', error)
        next('/pending')
        return
      }
    }
  }  // Check if route requires guest (login page)
  else if (to.matched.some(record => record.meta.requiresGuest)) {
    if (isAuthenticated) {
      // User is already authenticated, check if they can access the application
      const user = AuthService.getCurrentUser()
      try {
        const userStatus = await AuthService.getUserStatus(user.uid)
        
        // Update email verification status if it has changed
        if (user.emailVerified !== userStatus.emailVerified) {
          await AuthService.updateEmailVerificationStatus(user.uid, user.emailVerified)
          userStatus.emailVerified = user.emailVerified
        }
        
        // Check if email is verified and user has any permissions
        if (!userStatus.emailVerified || !Object.values(userStatus.permissions).some(p => p === true)) {
          next('/pending')
          return
        }
        
        // Find first available page and redirect there
        const availablePages = Object.keys(userStatus.permissions).filter(page => userStatus.permissions[page])
        if (availablePages.length > 0) {
          next(`/${availablePages[0]}`)
        } else {
          next('/pending')
        }
      } catch (error) {
        console.error('Error in guest route guard:', error)
        next('/pending')
      }
    } else {
      next()
    }
  }
  // Route doesn't require authentication
  else {
    next()
  }
})

export default router
